import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiTrash2, FiUpload, FiLink, FiPlus, FiBookOpen } from "react-icons/fi";
import { apiRequest } from "../../api";
import { PRIORITY_LABELS, PRIORITY_STYLES } from "../../chatbot/support";

const field = "w-full bg-navy border border-navy-border rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-light";
const btn = "px-3 py-1.5 rounded-md bg-white/5 border border-navy-border text-xs text-gray-200 inline-flex items-center gap-1.5 hover:border-accent-light disabled:opacity-40";
const panel = "bg-navy border border-navy-border rounded-xl p-5";
const SOURCE_LABELS = { text: "Typed", file: "File", url: "Web page" };

// What the support assistant knows: typed texts, uploaded files and imported web pages.
export function KnowledgePanel({ bot }) {
  const [docs, setDocs] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState({ text: "", bad: false });
  const fileRef = useRef(null);
  const base = `/core/admin/support/${bot.id}/knowledge/`;

  const load = useCallback(async () => {
    const { ok, data } = await apiRequest(base, { auth: true });
    setDocs(ok ? data : []);
  }, [base]);
  useEffect(() => { setDocs(null); load(); }, [load]);

  const run = async (kind, request) => {
    setBusy(kind);
    setMessage({ text: "", bad: false });
    const { ok, data } = await request();
    setBusy("");
    if (!ok) { setMessage({ text: (data && data.detail) || "That did not work.", bad: true }); return false; }
    setMessage({ text: `Learned "${data.title}" (${data.chunks} pieces).`, bad: false });
    load();
    return true;
  };
  const addText = async (e) => {
    e.preventDefault();
    if (await run("text", () => apiRequest(base, { method: "POST", auth: true, body: { title, content } }))) { setTitle(""); setContent(""); }
  };
  const addUrl = async (e) => {
    e.preventDefault();
    if (await run("url", () => apiRequest(`${base}url/`, { method: "POST", auth: true, body: { url } }))) setUrl("");
  };
  const addFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    await run("file", () => apiRequest(`${base}upload/`, { method: "POST", auth: true, body: form }));
  };
  const remove = async (doc) => {
    if (!window.confirm(`Remove "${doc.title}" from what the assistant knows?`)) return;
    await apiRequest(`${base}${doc.id}/`, { method: "DELETE", auth: true });
    load();
  };

  return (
    <div className={panel}>
      <h3 className="font-semibold mb-1 flex items-center gap-2"><FiBookOpen /> Knowledge base</h3>
      <p className="text-xs text-gray-400 mb-4">The assistant answers only from this (and from the notes above). Add your FAQs, policies, price lists, help pages. Changes work immediately.</p>
      <ul className="space-y-1.5 mb-5">
        {docs === null && <li className="text-sm text-gray-500">Loading...</li>}
        {docs && !docs.length && <li className="text-sm text-gray-500">Nothing yet. Without knowledge the assistant will hand every question to your team.</li>}
        {(docs || []).map((d) => (
          <li key={d.id} className="flex items-center justify-between gap-3 bg-white/5 rounded-lg px-3 py-2 text-sm">
            <div className="min-w-0"><div className="truncate">{d.title}</div><div className="text-[11px] text-gray-500">{SOURCE_LABELS[d.source] || d.source} - {d.chars.toLocaleString()} characters{d.source_ref ? ` - ${d.source_ref}` : ""}</div></div>
            <button type="button" className={btn} onClick={() => remove(d)} aria-label={`Remove ${d.title}`}><FiTrash2 /></button>
          </li>
        ))}
      </ul>
      {message.text && <p className={`text-sm mb-3 ${message.bad ? "text-red-300" : "text-emerald-300"}`} role="status">{message.text}</p>}
      <form onSubmit={addText} className="space-y-2 mb-4">
        <input className={field} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Returns policy)" aria-label="Title of the text" maxLength={200} />
        <textarea className={`${field} h-28`} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Paste the text here: a policy, a list of questions and answers, product details..." aria-label="Text to learn" />
        <button type="submit" className={btn} disabled={busy !== "" || content.trim().length < 20}><FiPlus /> {busy === "text" ? "Learning..." : "Add text"}</button>
      </form>
      <div className="grid sm:grid-cols-2 gap-4">
        <form onSubmit={addUrl} className="flex gap-2">
          <input className={field} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yoursite.com/help/shipping" aria-label="Web page address" />
          <button type="submit" className={`${btn} shrink-0`} disabled={busy !== "" || !url.trim()}><FiLink /> {busy === "url" ? "Reading..." : "Import page"}</button>
        </form>
        <div>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.pptx,.odt,.html,.htm,.txt,.md,.csv" className="hidden" onChange={addFile} aria-label="Upload a document" />
          <button type="button" className={btn} onClick={() => fileRef.current && fileRef.current.click()} disabled={busy !== ""}><FiUpload /> {busy === "file" ? "Reading..." : "Upload a file (PDF, Word, text...)"}</button>
        </div>
      </div>
    </div>
  );
}

const STATUS_LABELS = { open: "Open", pending: "Waiting", resolved: "Solved" };
const REASONS = { customer_request: "Asked for a person", unanswered: "Not answered", frustrated: "Upset customer", form: "Contact form" };

// The inbox: customers the assistant handed to a person.
export function TicketsPanel({ bot }) {
  const [filter, setFilter] = useState("open");
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const base = `/core/admin/support/${bot.id}/tickets/`;
  const load = useCallback(async () => {
    const { ok, data } = await apiRequest(`${base}${filter ? `?status=${filter}` : ""}`, { auth: true });
    setRows(ok ? data : []);
  }, [base, filter]);
  useEffect(() => { setRows(null); load(); }, [load]);

  const change = async (ticket, patch) => {
    await apiRequest(`${base}${ticket.id}/`, { method: "PATCH", auth: true, body: patch });
    load();
  };

  return (
    <div className={panel}>
      <h3 className="font-semibold mb-3">Tickets</h3>
      <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Ticket status">
        {[["open", "Open"], ["pending", "Waiting"], ["resolved", "Solved"], ["", "All"]].map(([key, label]) => (
          <button key={key || "all"} type="button" onClick={() => setFilter(key)} aria-pressed={filter === key} className={`px-3.5 py-1 rounded-full text-xs border ${filter === key ? "bg-accent-light text-navy border-accent-light font-semibold" : "bg-white/5 border-navy-border text-gray-300"}`}>{label}</button>
        ))}
      </div>
      {rows === null && <p className="text-sm text-gray-500">Loading...</p>}
      {rows && !rows.length && <p className="text-sm text-gray-500">No tickets here.</p>}
      <ul className="space-y-2.5">
        {(rows || []).map((t) => (
          <li key={t.id} className="rounded-lg bg-white/5 border border-navy-border p-3.5 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-gray-400">{t.reference}</span>
              <span className={`px-2 py-0.5 rounded-full text-[11px] ${PRIORITY_STYLES[t.priority]}`}>{PRIORITY_LABELS[t.priority]}</span>
              <span className="text-[11px] text-gray-500">{REASONS[t.reason] || t.reason} - {new Date(t.created_at).toLocaleString()}</span>
              <button type="button" className={`${btn} ml-auto`} onClick={() => setOpenId(openId === t.id ? null : t.id)}>{openId === t.id ? "Hide chat" : "Show chat"}</button>
            </div>
            <div className="font-medium mt-1.5">{t.subject}</div>
            <div className="text-gray-300 text-[13px]">
              {t.name || "(no name)"}{t.email && <> - <a className="underline" href={`mailto:${t.email}`}>{t.email}</a></>}{t.phone && <> - <a className="underline" href={`tel:${t.phone}`}>{t.phone}</a></>}
            </div>
            {t.message && <p className="text-gray-400 text-[13px] mt-1">{t.message}</p>}
            {openId === t.id && <pre className="mt-2 whitespace-pre-wrap font-sans text-[12.5px] text-gray-300 bg-black/30 rounded-lg p-3 max-h-60 overflow-y-auto">{t.summary || "(no chat)"}</pre>}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <select className="bg-navy border border-navy-border rounded-lg text-xs px-2 py-1.5" value={t.status} onChange={(e) => change(t, { status: e.target.value })} aria-label={`Status of ${t.reference}`}>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select className="bg-navy border border-navy-border rounded-lg text-xs px-2 py-1.5" value={t.priority} onChange={(e) => change(t, { priority: e.target.value })} aria-label={`Priority of ${t.reference}`}>
                {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input className={`${field} flex-1 min-w-[160px] py-1.5 text-xs`} defaultValue={t.notes} placeholder="Private note (saved when you leave the box)" aria-label={`Note for ${t.reference}`}
                onBlur={(e) => { if (e.target.value !== t.notes) change(t, { notes: e.target.value }); }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// How the assistant did in the last 30 days, and what customers asked that it could not answer.
export function InsightsPanel({ bot }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    apiRequest(`/core/admin/support/${bot.id}/insights/`, { auth: true }).then(({ ok, data: d }) => { if (alive) setData(ok ? d : undefined); });
    return () => { alive = false; };
  }, [bot.id]);
  if (data === null) return <div className={panel}><p className="text-sm text-gray-500">Loading...</p></div>;
  if (!data) return null;
  const stats = [["Answers", data.answers], ["Needed a person", `${data.handoffs} (${data.handoff_rate}%)`], ["Rated helpful", data.helpful], ["Rated not helpful", data.not_helpful], ["Open tickets", data.open_tickets]];
  return (
    <div className={panel}>
      <h3 className="font-semibold mb-3">Last {data.days} days</h3>
      <div className="grid sm:grid-cols-5 gap-3 mb-4">
        {stats.map(([t, v]) => <div key={t} className="bg-white/5 rounded-lg px-3 py-2.5"><div className="text-[11px] text-gray-400">{t}</div><div className="text-xl font-bold tabular-nums">{v}</div></div>)}
      </div>
      <h4 className="text-sm font-semibold mb-1.5">Questions it could not answer</h4>
      <p className="text-xs text-gray-400 mb-2">Add the answers to the knowledge base and these questions will be answered next time.</p>
      {!data.unanswered.length && <p className="text-sm text-gray-500">None so far.</p>}
      <ul className="space-y-1.5">
        {data.unanswered.map((u, i) => <li key={i} className="text-sm bg-white/5 rounded-lg px-3 py-2"><span dir="auto">{u.question}</span><span className="block text-[11px] text-gray-500">{u.why} - {new Date(u.at).toLocaleString()}</span></li>)}
      </ul>
    </div>
  );
}
